import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Building2, Users, Award, TrendingUp } from 'lucide-react';

const features = [
  {
    icon: Building2,
    title: "Established 2018",
    description: "Over 7 years of excellence in B2B electronics distribution"
  },
  {
    icon: Users,
    title: "Global Network",
    description: "Offices in India, Hong Kong & UAE serving clients worldwide"
  },
  {
    icon: Award,
    title: "Quality Assured",
    description: "Only genuine products from authorized brand partners"
  },
  {
    icon: TrendingUp,
    title: "Competitive Pricing",
    description: "Best wholesale rates for bulk orders and businesses"
  }
];

const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="about" className="py-20 md:py-32 relative overflow-hidden" data-testid="about-section">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-brand-primary rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand-accent rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" ref={ref}>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1758518729685-f88df7890776?w=800&q=80"
                alt="AJKTV Corporate Team"
                className="w-full h-auto object-cover rounded-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/60 to-transparent" />
            </div>
            
            {/* Floating Card */}
            <motion.div 
              className="absolute -bottom-6 -right-6 glass rounded-xl p-4 md:p-6"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-brand-accent/20 flex items-center justify-center">
                  <Award className="w-6 h-6 text-brand-accent" />
                </div>
                <div>
                  <div className="text-2xl font-outfit font-bold text-white">7+</div>
                  <div className="text-sm text-gray-400">Years of Excellence</div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Content Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="inline-block px-4 py-2 rounded-full bg-brand-primary/20 text-brand-accent text-sm font-medium mb-4">
              About Us
            </span>
            
            <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Your Trusted Partner in{' '}
              <span className="gradient-text">Global Electronics Trade</span>
            </h2>
            
            <p className="text-gray-300 text-lg mb-8 leading-relaxed">
              AJKTV Overseas Private Limited is a leading B2B distributor of consumer electronics 
              and IT products. Since 2018, we've been connecting businesses with premium technology 
              solutions from world-renowned brands.
            </p>

            <p className="text-gray-400 mb-8">
              With strategic offices in India, Hong Kong, and UAE, we offer seamless international 
              trade services, competitive pricing, and reliable logistics to businesses of all sizes.
            </p>

            {/* Features Grid */}
            <div className="grid sm:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                  className="flex gap-4"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-brand-surface flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-brand-accent" />
                  </div>
                  <div>
                    <h3 className="font-outfit font-semibold text-white mb-1">{feature.title}</h3>
                    <p className="text-sm text-gray-400">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
